class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.pendingCartRequests = 0;
    this.onCartRequestStart = this.handleCartRequestStart.bind(this);
    this.onCartRequestEnd = this.handleCartRequestEnd.bind(this);

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.initLoadingOverlay();
    window.addEventListener('animeal:cart:request-start', this.onCartRequestStart);
    window.addEventListener('animeal:cart:request-end', this.onCartRequestEnd);
  }

  disconnectedCallback() {
    window.removeEventListener('animeal:cart:request-start', this.onCartRequestStart);
    window.removeEventListener('animeal:cart:request-end', this.onCartRequestEnd);
  }

  setHeaderCartIconAccessibility() {
    // header.liquid and header-drawer.liquid (mobile menu) both render
    // their own cart icon with the same id="cart-icon-bubble" — a
    // querySelector() here would only wire up whichever one happens to
    // come first in the DOM, leaving the other to fall through to its
    // plain href and navigate to the full cart page instead of opening
    // the drawer. Wire up every one of them.
    document.querySelectorAll('#cart-icon-bubble').forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
    });

    // Delegated (capture phase) instead of per-element listeners: the icon
    // can be re-rendered or duplicated (mobile menu drawer, ajax refreshes,
    // other scripts calling stopPropagation), and a listener bound directly
    // to the anchor would be lost, letting the plain href open /cart.
    if (CartDrawer.cartIconDelegated) return;
    CartDrawer.cartIconDelegated = true;

    const findCartLink = (event) =>
      event.target.closest && event.target.closest('#cart-icon-bubble, .header__icon--cart');

    document.addEventListener(
      'click',
      (event) => {
        const cartLink = findCartLink(event);
        const drawer = document.querySelector('cart-drawer');
        if (!cartLink || !drawer || typeof drawer.open !== 'function') return;
        event.preventDefault();
        drawer.open(cartLink);
      },
      true
    );
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.code !== 'Space') return;
        const cartLink = findCartLink(event);
        const drawer = document.querySelector('cart-drawer');
        if (!cartLink || !drawer || typeof drawer.open !== 'function') return;
        event.preventDefault();
        drawer.open(cartLink);
      },
      true
    );
  }

  initLoadingOverlay() {
    const drawer = this.querySelector('#CartDrawer');
    if (!drawer || drawer.querySelector('.cart-drawer-loading')) return;

    const overlay = document.createElement('div');
    overlay.className = 'cart-drawer-loading hidden';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.innerHTML = `
      <div class="cart-drawer-loading__spinner" role="status" aria-label="Loading">
        <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
          <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
        </svg>
      </div>
    `;
    drawer.appendChild(overlay);
  }

  handleCartRequestStart() {
    this.pendingCartRequests += 1;
    this.setLoadingState(true);
  }

  handleCartRequestEnd() {
    this.pendingCartRequests = Math.max(0, this.pendingCartRequests - 1);
    if (this.pendingCartRequests === 0) {
      this.setLoadingState(false);
    }
  }

  setLoadingState(isLoading) {
    this.initLoadingOverlay();
    const overlay = this.querySelector('.cart-drawer-loading');
    if (!overlay) return;

    overlay.classList.toggle('hidden', !isLoading);
    this.classList.toggle('is-loading', isLoading);
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      setTimeout(() => {
        document.querySelectorAll('.custom__cart_item_remove')?.forEach((custRemoveBtn)=>{
          custRemoveBtn.addEventListener('click', function() {
              this.closest('.cart-item').querySelector('cart-remove-button').click();
              // console.log(this.closest('.cart-item').querySelector('cart-remove-button'));
              custRemovePouchItems();
          });
        });
      }, 500);
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, openDrawer = true) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    this.initLoadingOverlay();
    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      // Some callers (the product page's own add-to-cart) only want the
      // cart icon/drawer content refreshed in the background, not the
      // drawer popped open in the customer's face — the drawer content is
      // still kept fresh underneath for whenever they do open it.
      if (openDrawer) this.open();
      window.dispatchEvent(
        new CustomEvent('animeal:cart-drawer:rendered', {
          detail: {
            source: 'cart-drawer',
            cart: parsedState || null,
          },
        })
      );
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
